import { useEffect, useRef, useState } from 'react';
import { Dumbbell } from 'lucide-react';

const VIDEO_RE = /\.(mp4|webm|mov|m4v|ogv)(\?.*)?$/i;

// NOTE: 360p on-demand serving was disabled — transcoding inside the file request could exceed
// Cloudflare's ~100s limit (broken/slow/non-loading videos). Serve the original master directly.
export function optimized360(src) {
  return src;
}

// Neutral tile shown while media loads, and permanently if the file is missing or broken.
export function MediaPlaceholder({ className = '', label = '' }) {
  return (
    <div className={`flex flex-col items-center justify-center bg-[var(--c-film)] text-muted-foreground ${className}`} data-testid="exercise-media-placeholder">
      <Dumbbell className="w-5 h-5 opacity-60" />
      {label && <span className="text-[10px] mt-1 px-1 text-center">{label}</span>}
    </div>
  );
}

// Starts loading only when the tile is near the screen, so long lists don't fire dozens of requests at once.
function useNearScreen() {
  const ref = useRef(null);
  const [near, setNear] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (near || !ref.current) return undefined;
    const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { setNear(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [near]);
  return [ref, near];
}

// Renders exercise demonstration media. Videos play looping & muted; everything else renders as an image.
// The tile stays neutral until the first frame is ready (no black squares) and retries once on failure.
export function ExerciseMedia({ src, alt = '', className = '', fit = 'contain', rounded = false }) {
  const [ref, near] = useNearScreen();
  const [tries, setTries] = useState(0);
  const [ready, setReady] = useState(false);
  const failed = tries > 1;
  if (!src) return null;

  const url = tries === 1 ? `${src}${src.includes('?') ? '&' : '?'}r=1` : src;
  const fail = () => { setReady(false); setTries(t => t + 1); };
  const wrapper = `relative overflow-hidden flex items-center justify-center ${ready ? 'bg-[#05101C]' : ''} ${rounded ? 'rounded-xl' : ''} ${className}`;
  const fitClass = `${fit === 'cover' ? 'object-cover w-full h-full' : 'object-contain max-w-full max-h-full w-full h-full'} ${ready ? '' : 'opacity-0'}`;
  const isVideo = VIDEO_RE.test(src);

  return (
    <div ref={ref} className={wrapper} data-testid={failed ? 'exercise-media-error' : (isVideo ? 'exercise-media-video' : 'exercise-media-image')}>
      {!ready && <MediaPlaceholder className="absolute inset-0" label={failed ? 'No preview' : ''} />}
      {near && !failed && (isVideo ? (
        <video key={url} src={optimized360(url)} className={fitClass} autoPlay loop muted playsInline preload="auto"
          controls={false} onLoadedData={() => setReady(true)} onError={fail} />
      ) : (
        <img key={url} src={url} alt={alt} className={fitClass} loading="lazy"
          onLoad={e => (e.currentTarget.naturalWidth > 2 ? setReady(true) : setTries(2))} onError={fail} />
      ))}
    </div>
  );
}
